import {DailyChallenge, DailyHit, DailyStats} from "./daily.model";

export const DAILY_WIN_RATIO = 0.5;
const GERMAN_TIME_ZONE = 'Europe/Berlin';

/**
 * Returns the calendar day of the given date in german time formatted as YYYY-MM-DD.
 * The daily challenge changes at midnight german time.
 */
export const getGermanDateString = (date: Date = new Date()): string => {
  // en-CA formats dates as YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: GERMAN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

export const addDays = (dateString: string, days: number): string => {
  const date = new Date(`${dateString}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().substring(0, 10);
};

/**
 * Milliseconds until the next midnight in german time
 */
export const getMillisUntilNextGermanDay = (now: Date = new Date()): number => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: GERMAN_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(now);
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? 0);
  const elapsed = ((get('hour') * 60 + get('minute')) * 60 + get('second')) * 1000 + now.getMilliseconds();
  return 24 * 60 * 60 * 1000 - elapsed;
};

/**
 * Checks whether a song placed at the given index of the timeline is in chronological order.
 * The index -1 means before the first entry, index i means after the i-th entry.
 */
export const isPlacementCorrect = (timeline: DailyHit[], index: number, year: number): boolean => {
  const before = index >= 0 ? timeline[index] : undefined;
  const after = index + 1 < timeline.length ? timeline[index + 1] : undefined;
  return (!before || before.year <= year) && (!after || year <= after.year);
};

export const insertChronologically = (timeline: DailyHit[], hit: DailyHit): DailyHit[] => {
  const index = timeline.findIndex(entry => entry.year > hit.year);
  const result = [...timeline];
  result.splice(index === -1 ? timeline.length : index, 0, hit);
  return result;
};

/**
 * Builds the timeline of the player after the given results: only correctly placed songs stay on the timeline
 */
export const buildTimeline = (challenge: DailyChallenge, results: boolean[]): DailyHit[] => {
  return challenge.hits
    .filter((_, index) => results[index])
    .reduce((timeline, hit) => insertChronologically(timeline, hit), [challenge.anchor]);
};

export const countCorrect = (results: boolean[]): number => results.filter(r => r).length;

/** Results as emoji squares, e.g. 🟩🟥🟩 */
export const toSquares = (results: boolean[]): string => results.map(r => r ? '🟩' : '🟥').join('');

/** Number of correctly placed songs needed to win */
export const getWinTarget = (total: number): number => Math.ceil(total * DAILY_WIN_RATIO);

export const isWin = (results: boolean[], total: number): boolean => {
  return countCorrect(results) >= getWinTarget(total);
};

/** Percentage of won challenges, rounded */
export const getWinRate = (stats: DailyStats): number => {
  return stats.played ? Math.round(stats.wins / stats.played * 100) : 0;
};

/** Formats a duration as HH:MM:SS */
export const formatCountdown = (millis: number): string => {
  const totalSeconds = Math.max(0, Math.floor(millis / 1000));
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${pad(Math.floor(totalSeconds / 3600))}:${pad(Math.floor(totalSeconds / 60) % 60)}:${pad(totalSeconds % 60)}`;
};

/**
 * The streak is only still alive if the last win was today or yesterday
 */
export const getCurrentStreak = (stats: DailyStats, today: string): number => {
  if (stats.lastWinDate === today || stats.lastWinDate === addDays(today, -1)) {
    return stats.currentStreak;
  }
  return 0;
};

export const applyResult = (stats: DailyStats, date: string, won: boolean): DailyStats => {
  const played = stats.played + 1;
  if (!won) {
    return {...stats, played, currentStreak: 0};
  }
  const currentStreak = stats.lastWinDate === addDays(date, -1) ? stats.currentStreak + 1 : 1;
  return {
    currentStreak,
    bestStreak: Math.max(stats.bestStreak, currentStreak),
    lastWinDate: date,
    played,
    wins: stats.wins + 1
  };
};

export const buildShareText = (date: string, results: boolean[], streak: number, url: string): string => {
  const lines = [
    `Evergreens Daily 10 · ${date}`,
    `${toSquares(results)} ${countCorrect(results)}/${results.length}`
  ];
  if (streak > 0) {
    lines.push(`🔥 ${streak}`);
  }
  lines.push(url);
  return lines.join('\n');
};
